import { type AutoCompleteItem } from '@atlassian/eoc-auto-complete';
import { type IFilterValues } from '@atlassian/eoc-condition-filter';
import { type WeekDay } from '@atlassian/eoc-day-time-interval';
// this response is coming from webapi/user/notificationsInfo?isDeleted=false&userId=5bc5bdc9-f560-4865-b705-5f8ede05af9e address
// if we want to change availableActionTypes, we should make a new contract with backend.
export enum AVAILABLE_RULE_TYPES {
	'New Alert' = 0,
	'Schedule Start' = 3,
	'Schedule End' = 7,
	'Incoming Call Routing' = 8,
	'Acknowledged Alert' = 1,
	'Closed Alert' = 2,
	'Assigned Alert' = 5,
	'Add Note' = 6,
}

export enum OperationIds {
	Matches = 0,
	Contains = 1,
	StartsWith = 3,
	EndsWith = 4,
	Equals = 5,
	ContainsKey = 6,
	ContainsValue = 7,
	GreaterThan = 8,
	LessThan = 9,
	IsEmpty = 10,
	EqualsIgnoreWhitespace = 11,
}

export enum ReverseOperations {
	'matches' = 0,
	'contains' = 1,
	'starts-with' = 3,
	'ends-with' = 4,
	'equals' = 5,
	'contains-key' = 6,
	'contains-value' = 7,
	'greater-than' = 8,
	'less-than' = 9,
	'is-empty' = 10,
	'equals-ignore-whitespace' = 11,
}

export enum MatchConditionType {
	'match-all' = 0,
	'match-any-condition' = 1,
	'match-all-conditions' = 2,
}

export enum Operation {
	Matches = 'matches',
	Contains = 'contains',
	StartsWith = 'starts-with',
	EndsWith = 'ends-with',
	Equals = 'equals',
	ContainsKey = 'contains-key',
	ContainsValue = 'contains-value',
	GreaterThan = 'greater-than',
	LessThan = 'less-than',
	IsEmpty = 'is-empty',
	EqualsIgnoreWhitespace = 'equals-ignore-whitespace',
}

export interface Condition {
	order?: number;
	customKey?: string;
	field: string;
	expectedValue: string;
	not: boolean;
	operation: OperationIds;
	fieldFreeForm?: boolean;
}

export interface EditDescriptionEndpoint {
	preferenceId: string;
	nextPreferenceId: string | null;
}

export interface Rule {
	preferenceId: string;
	notificationRuleType?: string;
	order: number;
	scheduleNotifyTimes?: number[];
	scheduleIds?: string[];
	enabled: boolean;
	name: string;
	conditionMatchType: number;
	loopAfter: number;
	timeRestrictedInterval: TimeRestrictedInterval;
	conditions: Condition[];
	userId: string;
	rules: RuleMeta[];
	default: boolean;
}

export interface TimeRestriction {
	startHour: number;
	startMinute: number;
	endHour: number;
	endMinute: number;
	startDay?: WeekDay;
	endDay?: WeekDay;
}

export interface TimeRestrictedInterval {
	enabled: boolean;
	timeRestrictions: TimeRestriction[];
	appliedTimeZone: string;
}

// TODO: this is shared among multiple package, might unify
export type ContactMethodTypes = 'email' | 'sms' | 'mobile' | 'voice';

export interface RuleMeta {
	contactId?: string;
	ruleTime: number;
	enabled: boolean;
	ruleId?: string;
	mobileSound?: string;
	roleBasedContactDefinition?: {
		method: ContactMethodTypes;
		targetOrder: number;
	};
}

export interface Contact {
	contactId: string;
	method: 'EMAIL' | 'SMS' | 'MOBILE' | 'VOICE';
	enabled: boolean;
	disabledReason: string;
	addressDisplay: string;
	verified: boolean;
}

export type RuleType = keyof typeof AVAILABLE_RULE_TYPES;

interface NotificationPreferencesResponseBase {
	took: number;
	requestId: string;
}

// I initially thought of using the discriminated union for the rules as well,
// but because of the many layers of array mapping, it proved to be too cumbersome
// and not worth the effort.
export interface NotificationPreferencesResponse
	extends NotificationPreferencesResponseBase,
		Record<RuleType, Rule[]> {
	roleBasedNotificationName?: undefined;
}

export interface RoleBasedNotificationResponse
	extends NotificationPreferencesResponseBase,
		Record<RuleType, Rule[]> {
	roleBasedNotificationName: string;
	roleBasedNotificationRole: string;
}

export type ContactType = 'Email' | 'SMS' | 'Voice' | 'Mobile App';

export type Contacts = Record<ContactType, Contact[]>;

export interface ContactResponse {
	contacts: Contacts;
}

export interface CreateNotificationPreference {
	notificationRuleType: RuleType;
	scheduleNotifyTimes?: number[];
	scheduleIds?: { id: string }[];
	enabled: boolean;
	name: string;
	conditionMatchType: number;
	loopAfter: number;
	timeRestrictedInterval: TimeRestrictedInterval;
	conditions: Condition[];
	rules: RuleMeta[];
}

export interface UpdateNotificationPreference extends CreateNotificationPreference {
	preferenceId: string;
}

export interface UpdatePartialNotificationPreference {
	enabled: boolean;
}

export interface UpdatePartialNotificationRule {
	preferenceId: string;
	preferenceGroup: RuleType;
	ruleId: string;
	payload: Partial<Contact>;
}

export interface DeleteNotificationRule {
	preferenceId: string;
	preferenceGroup: RuleType;
	ruleId: string;
}

export interface CreateNotificationRule {
	preferenceId: string;
	preferenceGroup: RuleType;
	payload: {
		ruleTime: number;
		enabled: boolean;
		mobileSound: string;
		contactId: string;
	};
	onCompleted?: () => void;
}

export interface UpdateNotificationRule {
	preferenceId: string;
	ruleId: string;
	preferenceGroup: RuleType;
	payload: {
		ruleId: string;
		ruleTime: number;
		enabled: boolean;
		mobileSound: string;
		contactId: string;
	};
	onCompleted?: () => void;
}

export interface ContactMethodOptions extends Contact {
	label: string;
	value: string;
}

export interface MuteResponse {
	muteUntil: number;
	muted: boolean;
}

interface TimeRestrictions {
	startDay: number | null;
	endDay: number | null;
	startHour: number | null;
	startMinute: number | null;
	endHour: number | null;
	endMinute: number | null;
}

export interface QuietHoursResponse {
	quietHours: {
		enabled: boolean;
		timeRestrictions: Array<TimeRestrictions>;
		appliedTimeZone: string;
	};
}

export interface ForwardingRule {
	id: string;
	toUserId: string;
	fromUserId: string;
	startDate: number;
	endDate: number;
}

export interface ForwardingRulesResponse {
	values: ForwardingRule[];
}

export type ForwardingRulePayload = Pick<ForwardingRule, 'toUserId' | 'startDate' | 'endDate'>;

export interface ForwardingRuleFormState {
	toUser: AutoCompleteItem;
	startDate: string;
	endDate: string;
}

export interface ForwardingRuleHandlers {
	handleAddClick: () => void;
}

export interface FormData {
	dailyTimeRestrictionsFrom: string;
	dailyTimeRestrictionsTo: string;
	customTimeRestrictions: TimeRestriction[];
	filterAlerts: Pick<IFilterValues, 'conditions'> & {
		matchType: MatchConditionType;
	};
	loopAfter: string;
	name: string;
	notificationMethods: {
		editingMethodId?: string;
		isAddingMethod?: boolean;
		rules: RuleMeta[];
	};
	repeat: boolean;
	ruleType: { label: string; value: string };
	timeRestrictedIntervalEnabled: boolean;
	timeSlotRadio: string;
	schedule: { label: string; value: string }[];
	timings: string[];
}

export type ProductType = 'jsm' | 'compass';
